import React, { useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, Tabs, Row, Col, Divider } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Shop } from '../../../services/supplyChainService';

const { Option } = Select;
const { TabPane } = Tabs;

interface StoreFormProps {
    mode: 'create' | 'edit';
    initialValues: Partial<Shop> | null;
    onSave: (values: any) => Promise<void>;
    onCancel: () => void;
}

const StoreForm: React.FC<StoreFormProps> = ({ mode, initialValues, onSave, onCancel }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (mode === 'create') {
            form.resetFields();
            form.setFieldsValue({
                organization: 1,
                platform: 'amazon',
                region: 'NA',
                is_active: true
            });
        } else if (initialValues) {
            form.resetFields();
            form.setFieldsValue(initialValues);
        }
    }, [mode, initialValues, form]);

    const handleFinish = async () => {
        try {
            const values = await form.validateFields();
            await onSave(values);
        } catch (error) {
            // Form validation error, do nothing
        }
    };

    return (
        <Card
            title={
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={onCancel} />
                    <span>{mode === 'create' ? "新增店铺" : "编辑店铺"}</span>
                </Space>
            }
            extra={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleFinish}>保存</Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                {/* Hidden Fields */}
                <Form.Item name="organization" hidden><Input /></Form.Item>

                <Tabs defaultActiveKey="basic">
                    <TabPane tab="基础信息" key="basic">
                        <Row gutter={24}>
                            <Col span={12}>
                                <Card title="基本设置" bordered={false}>
                                    <Form.Item name="name" label="店铺名称" rules={[{ required: true, message: '请输入店铺名称' }]}>
                                        <Input placeholder="例如: Anker US Main Store" />
                                    </Form.Item>
                                    <Form.Item name="platform" label="平台" rules={[{ required: true }]}>
                                        <Select>
                                            <Option value="amazon">Amazon</Option>
                                            <Option value="walmart">Walmart</Option>
                                            <Option value="tiktok">TikTok</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="region" label="区域" rules={[{ required: true }]}>
                                        <Select>
                                            <Option value="NA">北美 (NA)</Option>
                                            <Option value="EU">欧洲 (EU)</Option>
                                            <Option value="FE">远东 (FE)</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="is_active" label="状态" valuePropName="checked">
                                        <Select>
                                            <Option value={true}>启用</Option>
                                            <Option value={false}>停用</Option>
                                        </Select>
                                    </Form.Item>
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card title="平台凭证 (SP-API)" bordered={false}>
                                    <Form.Item name="seller_id" label="Merchant Token (卖家ID)">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name="marketplace_id" label="Marketplace ID (市场ID)">
                                        <Input />
                                    </Form.Item>
                                </Card>
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane tab="主体资料" key="corporate">
                        <Row gutter={24}>
                            <Col span={12}>
                                <Form.Item name="company_name" label="公司名称 (主体)">
                                    <Input placeholder="营业执照上的公司名称" />
                                </Form.Item>
                                <Form.Item name="registration_number" label="统一社会信用代码/注册号">
                                    <Input />
                                </Form.Item>
                                <Form.Item name="legal_person" label="法人代表">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="company_address_cn" label="注册地址 (中文)">
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                                <Form.Item name="company_address_en" label="注册地址 (英文)">
                                    <Input.TextArea rows={2} placeholder="Amazon后台填写的英文地址" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider orientation="left">联系方式</Divider>
                        <Row gutter={24}>
                            <Col span={12}>
                                <Form.Item name="contact_email" label="店铺联系邮箱">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="contact_phone" label="店铺联系电话">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane tab="财务配置" key="finance">
                        <Row gutter={24}>
                            <Col span={12}>
                                <Card title="收款信息" bordered={false}>
                                    <Form.Item name="deposit_method" label="收款方式">
                                        <Select>
                                            <Option value="payoneer">Payoneer</Option>
                                            <Option value="worldfirst">WorldFirst</Option>
                                            <Option value="pingpong">PingPong</Option>
                                            <Option value="lianlian">LianLian</Option>
                                            <Option value="bank">本地银行卡</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="collection_account" label="收款账号详情">
                                        <Input placeholder="例如: Customer ID 或账号备注" />
                                    </Form.Item>
                                    <Form.Item name="bank_account_tail" label="收款账号尾号 (最后4位)">
                                        <Input style={{ width: 120 }} />
                                    </Form.Item>
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card title="付款信用卡" bordered={false}>
                                    <Form.Item name="credit_card_info" label="绑定信用卡">
                                        <Input placeholder="例如: Visa (尾号 8888) - Exp 12/28" />
                                    </Form.Item>
                                </Card>
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane tab="API 授权详情" key="api">
                        <div style={{ maxWidth: 600 }}>
                            <p style={{ color: '#666', marginBottom: 16 }}>
                                请在此处输入 Amazon Selling Partner API (SP-API) 的 LWA 授权信息。这些信息用于自动同步订单、库存和广告数据。
                            </p>
                            <Form.Item name="refresh_token" label="LWA Refresh Token">
                                <Input.Password placeholder="最关键的凭证，以 Atzr| 开头" />
                            </Form.Item>
                            <Form.Item name="client_id" label="LWA Client ID">
                                <Input.Password />
                            </Form.Item>
                            <Form.Item name="client_secret" label="LWA Client Secret">
                                <Input.Password />
                            </Form.Item>

                            <Divider />

                            <Form.Item name="aws_access_key" label="AWS Access Key ID">
                                <Input.Password />
                            </Form.Item>

                            <Form.Item name="aws_secret_key" label="AWS Secret Access Key">
                                <Input.Password />
                            </Form.Item>
                            <Form.Item name="role_arn" label="AWS IAM Role ARN">
                                <Input />
                            </Form.Item>
                        </div>
                    </TabPane>
                </Tabs>
            </Form>
        </Card>
    );
};

export default StoreForm;
